/* eslint-disable max-lines-per-function */
import {Tooltip} from 'components/Tooltip';
import {Typography} from 'components/Typography';
import {ProductCardActions} from 'modules/product-card-actions/components/ProductCardActions';
import {ListingCardRating} from 'modules/products/components/ListingCardRating';
import {ProductArticle} from 'modules/products/components/ProductArticle';
import {ProductImageSwitcher} from 'modules/products/components/ProductImageSwitcher';
import {ProductLabels} from 'modules/products/components/ProductLabels';
import {ProductProperties} from 'modules/products/components/ProductProperties';
import {IProductShort} from 'modules/products/types';
import Link from 'components/CustomRoutingLink';
import React, {FC, ReactNode, useMemo, useRef, useState} from 'react';
import style from './style.module.scss';
import {PrevDefaultLink} from 'components/PrevDefaultLink';
import {PriceAndBonuses} from 'modules/price-n-bonuses/components/PriceAndBonuses';
import {USED_IN} from 'modules/price-n-bonuses/constants';
import {TListViewType} from 'plugins/modules/listing/types';
import classNames from 'classnames';
import cs from 'classnames';
import {ListVisualProductCard} from '../ListVisualProductCard';
import {AddToFavoritesButton} from 'modules/product-card-actions/components/AddToFavoritesButton';
import {GiftCard} from 'modules/products/components/GiftCard';
import Icon from 'components/Icon';
import {useProductClickAnalytics} from 'modules/products/hooks/gtag/useProductClickAnalytics';
import {useIsMobileByScreen} from 'core/hooks/useIsMobileByScreen';
import {isArtProduct} from 'modules/products/helpers';
import {PRODUCT_ART_LINK} from 'modules/products/constants';
import {
    getProductCanonSection,
    getProductFineAndRarePropValue,
    getProductTopSection,
} from 'modules/product-card/helpers';
import pick from 'lodash/pick';
import {IAnalyticsMeta} from 'core/types';
import {formatPrice} from 'core/helpers';
import {SECTION_CODE} from 'modules/product-card/constants';
import {ALCOHOL, CULTIVATION_TYPE} from 'modules/product-card/constants/characteristics';
import {useIsPrive} from 'core/hooks/useIsPrive';
import {useIsomorphicLayoutEffect} from 'react-use';

export type TVisualProductCardProps = {
    isInStock?: boolean;
    listType: TListViewType;
    isFavourite?: boolean;
    isSimpleView?: boolean;
    isWishListPage?: boolean;
    withActions?: boolean;
    analytics?: IAnalyticsMeta;
    withMountedState?: boolean;
};

export type TProps = IProductShort & TVisualProductCardProps;

export const VisualProductCard: FC<TProps> = (props) => {
    const {
        id,
        url,
        offerId,
        price,
        media,
        title,
        labels,
        listType,
        isFavourite = false,
        isSimpleView = false,
        ratings,
        reviewsCount,
        properties,
        isWishListPage,
        qtyPerUOM,
        canBuy,
        canReserve,
        sections,
        withActions = true,
        analytics,
    } = props;

    const [isMounted, setIsMounted] = useState(false);

    useIsomorphicLayoutEffect(() => {
        setIsMounted(true);
    }, []);

    const isMobile = useIsMobileByScreen();

    const isGiftCard = false;

    const gtmProductClick = useProductClickAnalytics();

    const isArt = isArtProduct(properties);
    const isPrive = useIsPrive();

    const ref = useRef<HTMLDivElement>(null);
    const {images, imagePreview} = media || {};

    const isFineAndRareCategory = useMemo(() => {
        return getProductFineAndRarePropValue(properties);
    }, [properties]);

    const mobileIcon = useMemo(() => {
        if (isFineAndRareCategory) {
            return <Icon className={style.fineandrareIconMobile} icon={'fineandrare'} />;
        }

        if (isArt) {
            return (
                <div className={style.artCollection}>
                    <Tooltip
                        className={style.artTooltip}
                        id={offerId.toString() + '/art'}
                        overlay={
                            <div className={style.artOverlay}>
                                Находится в нашей специальной коллекции{' '}
                                <Link href={PRODUCT_ART_LINK}>Simple A.R.T.</Link>
                            </div>
                        }
                    >
                        <div className={style.artCollectionTitle}>A.R.T.</div>
                    </Tooltip>
                </div>
            );
        }

        return <div />;
    }, [isArt, isFineAndRareCategory, offerId]);

    const topSection = useMemo(
        () => sections?.map(getProductTopSection).find((section) => section?.isTop)?.code ?? sections?.[0]?.code,
        [sections],
    );

    const canonSection = useMemo(() => getProductCanonSection(sections), [sections]);

    const productImages = useMemo(
        () =>
            (images?.length ? [imagePreview, ...images] : [imagePreview]).filter(
                (img): img is string => 'string' === typeof img,
            ),
        [imagePreview, images],
    );

    const renderFavoriteButton = useMemo<ReactNode>(
        () => (
            <AddToFavoritesButton
                id={id}
                className={style.favoriteBtn}
                isFavourite={isFavourite}
                product={pick(props, ['id', 'name', 'sections', 'price', 'brand', 'properties'])}
                analytics={analytics}
            />
        ),
        [id, isFavourite, props, analytics],
    );

    const renderRemoveButton = useMemo<ReactNode>(
        () => (
            <button className={style.removeBtn}>
                <Icon icon={'delete'} />
            </button>
        ),
        [],
    );

    const isProductAvailable = isPrive ? canBuy : canBuy || canReserve;

    const basePrice = formatPrice(String(price.base?.price));

    const renderActions = useMemo<ReactNode>(
        () => (
            <>
                {!isProductAvailable ? (
                    <div className={cs(style.notInStock, 'notInStock')}>
                        <span data-skeleton-item={true} className={style.notInStockCaption}>
                            Этот товар закончился
                        </span>
                        {price.base?.price && (
                            <Typography className={cs(style.notInStockPrice, 'notInStockPrice')} variant={'price-24-b'}>
                                {basePrice} ₽
                            </Typography>
                        )}
                    </div>
                ) : (
                    <PriceAndBonuses
                        {...price}
                        isHorizontalView={'list' === listType}
                        labels={labels}
                        isOutOfStock={!isProductAvailable}
                        className={cs(style.visualProductCardPriceContainer, 'product-card-price-bonuses')}
                        qtyPerUOM={qtyPerUOM}
                        usedIn={USED_IN.VISUAL_PREVIEW}
                    />
                )}
                {withActions && (
                    <ProductCardActions
                        {...props}
                        className={style.visualProductCardActions}
                        isHorizontal={true}
                        stretched={true}
                        variant={listType}
                        analytics={analytics}
                    />
                )}
            </>
        ),
        [isProductAvailable, price, basePrice, labels, qtyPerUOM, withActions, listType, analytics, props],
    );

    const renderTitle = useMemo(
        () => (
            <Typography
                className={classNames(style.visualProductCardTitle, 'visual-product-card-title')}
                data-skeleton-item={true}
                element={'div'}
                variant={'snippet-title-16-r'}
            >
                <span
                    dangerouslySetInnerHTML={{
                        ['__html']: title ?? '',
                    }}
                />
            </Typography>
        ),
        [title],
    );

    const renderProperties = useMemo(() => {
        // eslint-disable-next-line no-shadow
        return properties?.map((property) => {
            if (
                ALCOHOL.KEY === property.code &&
                0 === Number(property.value) &&
                [SECTION_CODE.VINO, SECTION_CODE.CHAMPAGNE].includes(topSection)
            ) {
                return (
                    <div key={property.code} className={style.noAlcohol}>
                        0%
                    </div>
                );
            }

            if (
                CULTIVATION_TYPE.KEY === property.code &&
                'object' === typeof property.value &&
                'value' in property.value &&
                property.value.value
            ) {
                const propertyText = property.value.value;
                return (
                    <Tooltip
                        key={property.code}
                        overlay={<Typography>{propertyText}</Typography>}
                        className={style.sustainableTooltip}
                    >
                        <Icon className={style.productPropIcon} icon={'organic'} />
                    </Tooltip>
                );
            }

            return null;
        });
    }, [properties, topSection]);

    const renderImageSection = useMemo(
        () => (
            <div className={style.visualProductCardImageList}>
                <div className={style.infoBlock} data-skeleton-item={true}>
                    {!isFineAndRareCategory ? (
                        <>
                            {0 < ratings.length && (
                                <ListingCardRating
                                    id={id}
                                    ratings={ratings}
                                    reviewsCount={reviewsCount ?? 0}
                                    url={`${url}reviews`}
                                    analytics={analytics}
                                />
                            )}
                            {0 < properties.length && renderProperties}
                        </>
                    ) : (
                        <Icon className={style.fineandrareIconDesktop} icon={'fineandrare'} />
                    )}
                </div>
                {url && (
                    <PrevDefaultLink href={url} onClick={gtmProductClick(props, analytics)} prefetch={false}>
                        <ProductImageSwitcher className={style.images} imageList={productImages} />
                    </PrevDefaultLink>
                )}
            </div>
        ),
        [
            isFineAndRareCategory,
            ratings,
            id,
            reviewsCount,
            url,
            analytics,
            properties.length,
            renderProperties,
            gtmProductClick,
            props,
            productImages,
        ],
    );

    const renderCard = useMemo(() => {
        return (
            <>
                <div className={style.outerWrapper}>
                    <div className={style.cardContainer} data-skeleton-item={true}>
                        <div className={style.topBlock}>
                            <div data-skeleton-item={true} className={style.articleTitleMobile}>
                                {id && <ProductArticle article={id} />}
                            </div>
                            {mobileIcon}
                            {!isSimpleView && !isWishListPage ? renderFavoriteButton : renderRemoveButton}
                        </div>
                        {renderImageSection}
                        <div className={style.labels}>
                            <ProductLabels isShortForm={true} labels={labels ?? []} id={id} analytics={analytics} />
                        </div>
                        {url && (
                            <PrevDefaultLink
                                href={url}
                                fullReload={false}
                                onClick={gtmProductClick(props)}
                                prefetch={false}
                                className={style.visualProductCardInfoContainer}
                            >
                                {renderTitle}
                            </PrevDefaultLink>
                        )}
                    </div>
                    {/* свойства для вертикального сниппета */}
                    <div
                        className={classNames(style.visualProductSubCategoryContainer, 'subcategory-container')}
                        data-skeleton-item={true}
                    >
                        <ProductProperties
                            id={id}
                            analytics={analytics}
                            properties={properties ?? []}
                            url={`/catalog/${topSection}`}
                            sections={sections}
                            topSection={topSection}
                            canonSection={canonSection}
                        />
                    </div>
                </div>
                <div className={style.actions}>{renderActions}</div>
            </>
        );
    }, [
        id,
        analytics,
        mobileIcon,
        isSimpleView,
        isWishListPage,
        renderFavoriteButton,
        renderRemoveButton,
        renderImageSection,
        labels,
        url,
        gtmProductClick,
        props,
        renderTitle,
        properties,
        topSection,
        sections,
        canonSection,
        renderActions,
    ]);

    return (
        <>
            <div
                className={classNames(style.visualProductCard, 'visual-product-card', {
                    [style[listType]]: 'tile' !== listType && !isMobile,
                })}
                ref={ref}
            >
                <div className={style.visualProductCardFrameContainer}>
                    {isGiftCard ? (
                        <div className={style.visualProductCardFrameContainer}>
                            <GiftCard actions={renderActions} imageList={images} title={renderTitle}>
                                {renderCard}
                            </GiftCard>
                        </div>
                    ) : (
                        <>
                            {'list' === listType && !isMobile && isMounted ? (
                                <ListVisualProductCard
                                    {...props}
                                    key={`list-${listType}`}
                                    actions={renderActions}
                                    id={id}
                                    favouriteButton={renderFavoriteButton}
                                    images={productImages}
                                    isInStock={isProductAvailable}
                                    titleNode={renderTitle}
                                    topSection={topSection}
                                    canonSection={canonSection}
                                    analytics={analytics}
                                />
                            ) : (
                                renderCard
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};
